import type { DomainGuid } from '@/features/execute-calculation-set/overmind/calculation';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import {
  errorPointLine,
  goodPointLine,
  warningPointLine
} from './pointLineStyles';
import type {
  HighlightStates,
  ColorStyles,
  ColorStyle
} from './styleMapElement';

export function isInitiatingGridElement(initiatingElementId: DomainGuid, renderedElementId: DomainGuid) {
  return initiatingElementId === renderedElementId;
}

export type MapElementType = 'Point' | 'Polygon' | 'LineString';
export type RenderedElementType = {
  id: DomainGuid;
  type: MapElementType;
};
export class RenderedElement {
  constructor(
    public readonly id: DomainGuid,
    public readonly type: MapElementType
  ) {}

  static fromFeature(feature: FeatureWithIDs): RenderedElementType {
    return new RenderedElement(
      feature.gc.globalId,
      feature.geometry.type as MapElementType
    );
  }

  static create(id: DomainGuid, type: MapElementType): RenderedElementType {
    return new RenderedElement(id, type);
  }
}

export class MapElementDesigner implements ForStylingMapElements {
    private shouldUseInitiatingObjectHighlighting?: boolean
     
  private constructor(
    private readonly renderedElement: RenderedElementType,
    private readonly selectedItemInMap: DomainGuid | undefined,
    private readonly initatingElement: DomainGuid,
    private readonly highlightState: HighlightStates,
    readonly colors: ColorStyles,
    private readonly externalNetworkEntryPoint?: DomainGuid | undefined
  ) {}

  public static create(
    renderedElement: RenderedElementType,
    selectedItemInMap: DomainGuid | undefined,
    initatingElement: DomainGuid,
    highlightState: HighlightStates,
    colors: ColorStyles,
    externalNetworkEntryPoint?: DomainGuid | undefined,
    shouldUseInitiatingObjectHighlighting?: boolean
  ) {
    const designer = new MapElementDesigner(
      renderedElement,
      selectedItemInMap,
      initatingElement,
      highlightState,
      colors,
      externalNetworkEntryPoint
    );
    designer.shouldUseInitiatingObjectHighlighting = shouldUseInitiatingObjectHighlighting;

    return designer;
  }

  public static createForLvShortCircuit(
    renderedElement: RenderedElementType,
    selectedItemInMap: DomainGuid | undefined,
    initatingElement: DomainGuid,
    highlightState: HighlightStates,
    colors: ColorStyles,
    shouldUseInitiatingObjectHighlighting?: boolean
    
  ) {
    const standardDesigner = new MapElementDesigner(
      renderedElement,
      selectedItemInMap,
      initatingElement,
      highlightState,
      colors
    );
    standardDesigner.shouldUseInitiatingObjectHighlighting = shouldUseInitiatingObjectHighlighting;

    return ForLvShortCircuit.decorate(standardDesigner);
  }

  get isHighlightActive() {
    return this.highlightState.type === 'SOME_HIGHLIGHTED';
  }

  get evaluatedStatusForFeature() {
    if (this.highlightState.type === 'SOME_HIGHLIGHTED') {
      return this.highlightState.context.highlighted[this.renderedElement.id];
    }
  }

  get isFeatureSelected() {
    return (
      this.selectedItemInMap != null &&
      this.renderedElement.id === this.selectedItemInMap
    );
  }

  get isConnectedWithExternalNetwork() {
    return (
      !!this.externalNetworkEntryPoint &&
      this.renderedElement.id === this.externalNetworkEntryPoint
    );
  }

  styleLineString() {
    const weight = this.isFeatureSelected ? 10 : 5;

    if (this.isHighlightActive) {
      const isFeatureHighlighted = this.evaluatedStatusForFeature != null;
      if (isFeatureHighlighted) {
        const styledLine = this.styleEvaluatedLine(weight);
        if(isInitiatingGridElement(this.initatingElement, this.renderedElement.id) && this.shouldUseInitiatingObjectHighlighting) {
          const styledInitiatingElement = styledLine;
          return styledInitiatingElement;
        }

        return styledLine;
      }
    }

    if (this.isConnectedWithExternalNetwork) {
      return { ...goodPointLine, color: 'black', weight };
    }
    return this.colors.subnetPointLine;
  }

  private styleEvaluatedLine(weight: number) {
    switch (this.evaluatedStatusForFeature) {
      case 'Bad':
        return {
          ...errorPointLine,
          weight,
          color: '#ff0000',
          fillColor: '#ff0000'
        };
      case 'Warning':
        return {
          ...errorPointLine,
          weight,
          color: '#ecb67f',
          fillColor: '#ecb67f'
        };
      case 'Good':
        return {
          ...goodPointLine,
          weight,
          fillColor: '#008000',
          color: '#008000'
        };
      default:
        return this.colors.subnetPointLine;
    }
  }

  stylePoint() {
    const isInitiating = isInitiatingGridElement(this.initatingElement, this.renderedElement.id) && this.shouldUseInitiatingObjectHighlighting;
    if (this.isFeatureSelected) {
      if(isInitiating) {
        return this.styleInitiatingEvaluatedFeautre(this.colors.selectedPoint);
      } else {
        return this.colors.selectedPoint
      }
    }

    if (this.isHighlightActive && this.evaluatedStatusForFeature != null) {
      const styledFeature = this.styleEvaluatedFeature();
      if(isInitiating) {
        return this.styleInitiatingEvaluatedFeautre(styledFeature);
      }

      return styledFeature;
    }
    return this.colors.subnetPointLine;
  }

  private styleInitiatingEvaluatedFeautre(style: ColorStyle) {
    return {
      ...style,
      color: '#000000',
      weight: style.weight + 2
    };
  }
  private styleEvaluatedFeature() {
    switch (this.evaluatedStatusForFeature) {
      case 'Bad':
        return errorPointLine;
      case 'Warning':
        return warningPointLine;
      case 'Good':
        return goodPointLine;
      default:
        return this.colors.subnetPointLine;
    }
  }

  stylePolygon() {
    if (this.isFeatureSelected) {
      return this.colors.selectedArea;
    }

    return this.colors.subnetArea;
  }
}

class ForLvShortCircuit implements ForStylingMapElements {
  private constructor(
    private readonly mapElementDesigner: MapElementDesigner
  ) {}

  public static decorate(mapElementDesigner: MapElementDesigner) {
    return new ForLvShortCircuit(mapElementDesigner);
  }

  styleLineString() {
    if (this.mapElementDesigner.isFeatureSelected) {
      return this.mapElementDesigner.colors.selectedLine;
    }
    return this.mapElementDesigner.colors.subnetPointLine;
  }

  stylePoint() {
    return this.mapElementDesigner.stylePoint();
  }

  stylePolygon() {
    return this.mapElementDesigner.stylePolygon();
  }
}

export type ForStylingMapElements = {
  styleLineString: () => ColorStyle | undefined;
  stylePoint: () => ColorStyle | undefined;
  stylePolygon: () => ColorStyle | undefined;
};
